import { useState, useEffect } from "react";
import axios from "axios";
import { ShieldCheck, Loader2, Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleLabels = {
  admin: "Admin",
  hr: "HR",
  finance: "Finance",
};

const PermissionMatrixDialog = ({ open, onOpenChange }) => {
  const { user, refreshPermissions } = useAuth();
  const isManager = user?.role === "manager";
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null); // "role:perm"
  const [available, setAvailable] = useState({});
  const [roles, setRoles] = useState([]);
  const [matrix, setMatrix] = useState({});

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/permissions`);
      setAvailable(res.data.available || {});
      setRoles(res.data.configurable_roles || []);
      setMatrix(res.data.matrix || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load permissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const toggle = async (role, permKey, nextEnabled) => {
    if (!isManager) return;
    const key = `${role}:${permKey}`;
    setSaving(key);
    // optimistic update
    setMatrix((prev) => ({
      ...prev,
      [role]: { ...prev[role], [permKey]: nextEnabled },
    }));
    try {
      await axios.put(`${API}/permissions/${role}/${permKey}`, { enabled: nextEnabled });
      toast.success(
        `${roleLabels[role] || role}: ${nextEnabled ? "granted" : "revoked"} ${available[permKey] || permKey}`
      );
      // refresh current user's effective perms so buttons re-gate live
      refreshPermissions();
    } catch (e) {
      // revert
      setMatrix((prev) => ({
        ...prev,
        [role]: { ...prev[role], [permKey]: !nextEnabled },
      }));
      toast.error(e.response?.data?.detail || "Failed to update permission");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl border-[#E4E4E7] rounded-sm max-h-[85vh] overflow-y-auto"
        data-testid="permission-matrix-dialog"
      >
        <DialogHeader className="border-b border-[#E4E4E7] pb-4">
          <DialogTitle
            className="text-2xl font-semibold tracking-tight flex items-center gap-2"
            style={{ fontFamily: "Manrope" }}
          >
            <ShieldCheck className="w-6 h-6" />
            Role Permissions
          </DialogTitle>
          <DialogDescription className="text-sm text-[#71717A]">
            {isManager
              ? "Toggle what Admin, HR, and Finance are allowed to do. Manager always has full access."
              : "Read-only view of the permission matrix (manager-only editing)."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-[#71717A]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading...
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Header row */}
            <div
              className="grid gap-3 items-end pb-2 border-b border-[#E4E4E7]"
              style={{ gridTemplateColumns: `1fr repeat(${roles.length}, 100px)` }}
            >
              <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">
                Permission
              </div>
              {roles.map((r) => (
                <div
                  key={r}
                  className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] text-center"
                >
                  {roleLabels[r] || r}
                </div>
              ))}
            </div>

            {Object.entries(available).map(([permKey, label]) => (
              <div
                key={permKey}
                className="grid gap-3 items-center py-2 border-b border-[#F4F4F5] last:border-0"
                style={{ gridTemplateColumns: `1fr repeat(${roles.length}, 100px)` }}
                data-testid={`perm-row-${permKey}`}
              >
                <div>
                  <div className="text-sm font-semibold text-[#09090B]">{label}</div>
                  <div className="text-[10px] text-[#71717A] font-mono">{permKey}</div>
                </div>
                {roles.map((role) => {
                  const enabled = !!matrix[role]?.[permKey];
                  const isSaving = saving === `${role}:${permKey}`;
                  return (
                    <div key={role} className="flex justify-center">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={enabled}
                        disabled={!isManager || isSaving}
                        onClick={() => toggle(role, permKey, !enabled)}
                        className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none ${
                          enabled ? "bg-[#059669]" : "bg-[#71717A]"
                        } ${!isManager ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                        data-testid={`perm-toggle-${role}-${permKey}`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                            enabled ? "translate-x-5" : "translate-x-0"
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}

            {!isManager && (
              <div className="mt-2 text-xs text-[#71717A] flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Manager role required to edit permissions.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PermissionMatrixDialog;
